import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Hash, ArrowLeft, Play, Heart, Gift, Loader2 } from "lucide-react";

/**
 * TagFeed — vertical grid of reels for a specific #hashtag. Tapping a card
 * takes the user to the reels feed with `?reel={id}` so the video plays with
 * all the normal engagement affordances.
 */
export default function TagFeed() {
  const { name } = useParams();
  const nav = useNavigate();
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    api.get(`/reels/by-tag/${encodeURIComponent(name)}`)
      .then(r => setReels(r.data || []))
      .catch(() => setReels([]))
      .finally(() => setLoading(false));
  }, [name]);

  const display = "#" + (name || "").replace(/^#/, "");

  return (
    <div className="px-4 py-6 md:max-w-4xl md:mx-auto" data-testid="tag-feed-page">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => nav(-1)} data-testid="tag-back"
          className="p-2 rounded-full bg-white/8 hover:bg-white/12"><ArrowLeft className="w-4 h-4"/></button>
        <div className="flex-1">
          <h1 className="font-outfit font-extrabold text-3xl tracking-tighter text-[#A855F7] flex items-center gap-1.5">
            <Hash className="w-6 h-6"/>{display.replace(/^#/, "")}
          </h1>
          <div className="text-xs text-white/55 mt-0.5">{reels.length} reel{reels.length !== 1 ? "s" : ""} tagged</div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-[#A855F7]"/></div>
      ) : reels.length === 0 ? (
        <div className="text-center py-14 text-white/60">
          <div className="text-5xl mb-3">🌱</div>
          <div className="font-bold text-lg">No reels yet for {display}</div>
          <div className="text-sm text-white/50 mt-1">Be the first to tag one!</div>
          <Link to="/upload" className="inline-block mt-4 px-4 py-2 rounded-full bg-gradient-to-r from-[#A855F7] to-[#FF2A5F] text-white text-sm font-bold" data-testid="tag-upload-cta">Upload a reel →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-1.5" data-testid="tag-grid">
          {reels.map(r => (
            <Link key={r.id} to={`/?reel=${r.id}`} data-testid={`tag-card-${r.id}`}
              className="relative aspect-[9/16] rounded-lg overflow-hidden bg-black active:scale-95 transition group">
              {r.thumbnail_url ? (
                <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl">📹</div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent"/>
              <div className="absolute bottom-1 left-1 right-1 text-[10px] flex items-center gap-1.5">
                <span className="flex items-center gap-0.5"><Play className="w-2.5 h-2.5"/>{r.views || 0}</span>
                <span className="flex items-center gap-0.5"><Heart className="w-2.5 h-2.5 text-[#FF2A5F]"/>{r.likes || 0}</span>
                {(r.gifts_received || 0) > 0 && <span className="flex items-center gap-0.5 text-[#FF7A00]"><Gift className="w-2.5 h-2.5"/>{r.gifts_received}</span>}
              </div>
              <div className="absolute top-1 left-1 flex items-center gap-1 bg-black/60 rounded-full pr-1.5">
                <img src={r.user?.avatar} alt="" className="w-4 h-4 rounded-full"/>
                <span className="text-[9px] font-bold truncate max-w-[70px]">@{r.user?.username || "user"}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
